import { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { CrossSvg } from '../../svgs/Svgs'
import { AllFilterType, AppliedFilter, FilterListType, SelectedFilter } from '../types';
import AdvanceFilterModal from './AdvanceFilterModal';
import { showToastMessage } from '../../../../utils/commonFunctions';
import './../CustomFilter.scss';
import { PlusSvg } from '../../../command_center/custom_components/Svgs';
import CustomToolTip from '../../CustomToolTip';

interface Props {
    filterIcon: React.ReactNode;
    hideFilterIconOnOpen?: boolean;
    filterList: FilterListType | null;
    applyFilter: (data: any) => void;
    allFilters: { [key: string]: any };
    setAllFilters: (filters: { [key: string]: any }) => void;
    filterCase?: string;
    boundingScreen: string;
}

/**
 * Helper to avoid repeating CustomToolTip + span code.
 */
interface FilterSpanProps {
    title: string;
    onClick?: (e: any) => void;
    children: React.ReactNode;
}
const FilterSpan: FC<FilterSpanProps> = ({ title, onClick, children }) => (
    <CustomToolTip title={title} key={title} placement="top">
        <span
            className='filter__text__container'
            onClick={onClick}
        >
            {children}
        </span>
    </CustomToolTip>
);

const AdvanceFilter: FC<Props> = ({ filterIcon, hideFilterIconOnOpen, applyFilter, filterList, allFilters, setAllFilters, filterCase, boundingScreen }) => {

    const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([])
    const [selectedFilter, setSelectedFilters] = useState<SelectedFilter | null>(null)
    const [editingFilter, setEditingFilter] = useState<SelectedFilter | null>(null) // to store original filter which was editing
    const [openOptions, setOpenOptions] = useState<'Property' | 'Operator' | 'Value' | null>(null) //which options modal to open
    const [showFilterCount, setShowFilterCount] = useState(false) // how show collapsed view of filter applied
    const [isFilterExpanded, setIsFilterExpanded] = useState(false) // state to show filter expanded
    const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0, right: 0 });
    const [inputValue, setInputValue] = useState<string>("");
    const filterContainerRef = useRef<HTMLDivElement>(null);
    const isInput = useMemo(() => {
        const valueField = filterList?.options.find(
            option => option.label === selectedFilter?.properties
        )?.properties?.valueField;

        return valueField === "text" || valueField === "range";
    }, [filterList, selectedFilter]);


    //to reset the filters on click of reset
    useEffect(() => {
        if (allFilters?.advanceFilters?.length === 0) {
            const appliedFilterContainer = filterContainerRef.current
            if (appliedFilterContainer) {
                appliedFilterContainer.classList.remove('filter__applied')
                appliedFilterContainer.classList.remove('active')
            }
            setAppliedFilters([])
        } else if (allFilters?.advanceFilters?.length > 0){
            if (filterCase === 'console') {
                setAppliedFilters(allFilters?.advanceFilters)
            }
        }
    }, [allFilters])

    //to show the buttons without flickering
    useLayoutEffect(() => {
        const container = filterContainerRef.current;
        if (!container) return;

        const updateStates = () => { //calls after the DOM container changes
            const isActiveNow = container.classList.contains('active');
            setIsFilterExpanded(isActiveNow);

            if (filterCase === 'simulate' || filterCase === 'ucp') {
                setShowFilterCount(true);
            } else {
                setShowFilterCount(!isActiveNow && appliedFilters?.length > 0);
            }
        };

        const observer = new MutationObserver(updateStates);
        observer.observe(container, { attributes: true, attributeFilter: ['class'] });

        // Initial check
        updateStates();

        return () => observer.disconnect();
    }, [appliedFilters, filterCase]);


    const handlePopupPosition = (targetRect: DOMRect) => {
    const modalRect = filterContainerRef.current?.getBoundingClientRect();
    const boundingElement = document.querySelector(`.${boundingScreen}`);

    if (filterCase === 'validate') {
        const right = window.innerWidth - targetRect.right;
        setPopupPosition({
            top: targetRect.bottom + 8,
            left: targetRect.left,
            right,
        });
        return;
    }

    if (!modalRect || !boundingElement) return;

    const boundingRect = boundingElement.getBoundingClientRect();
    let left;

        // Special handling for 'ucp' filterCase to prevent overflow
        if (filterCase === 'ucp') {
            left = targetRect.left - modalRect.left + 895 - 450;
        } else if (filterCase === 'report' || targetRect.right + 450 > boundingRect.right) {
            // Intelligent positioning when near the right edge of bounding container
            left = targetRect.left - modalRect.left + 895 - 440;
        } else {
            // Original logic for other cases
            if (targetRect?.x - 500 - 10 > boundingRect.x + 50) {
                left = targetRect.left - modalRect.left + 895 - 450; //if going out of bounding screen
            } else {
                left = targetRect.left - modalRect.left + 895;
            }
        }

        setPopupPosition({
            top: targetRect.bottom - modalRect.top + 8,
            left: left,
            right: 0,
        });
    };

    useEffect(() => {
        const appliedFilterContainer = filterContainerRef.current
        const modal = filterContainerRef.current?.querySelector('.advance__filter__modal__container')
        const clickedOutside = (e: any) => {
            if (!appliedFilterContainer) return
            else if (modal && modal?.contains(e.target)) return
            else if (!appliedFilterContainer?.contains(e.target)) {
                // setSelectedFilters(null) May require in future
                appliedFilterContainer.classList.remove('active')
                const button = filterContainerRef.current?.querySelector('.filter__icon__container')
                if (!button) return
                const targetRect = button.getBoundingClientRect();
                handlePopupPosition(targetRect)
            }
        }
        document.addEventListener('mousedown', clickedOutside)
        return () => {
            document.removeEventListener('mousedown', clickedOutside)
        }
    }, [])

    const handleOpenModal = (openModal, openFilterRow, e) => { //flag is to open or expand respective
        const appliedFilterContainer = filterContainerRef.current;
        const modal = filterContainerRef.current?.querySelector('.advance__filter__modal__container');
        if (!modal || !appliedFilterContainer) return
        if (openFilterRow) appliedFilterContainer.classList.add('active')
        if (openModal) {
            const targetRect = (e.target as HTMLElement).getBoundingClientRect();
            handlePopupPosition(targetRect)
            modal.classList.add('active__modal')
        }
    }

    const onSelect = (name: string, value: any) => {
        if (name === 'properties') {
            setSelectedFilters({
                properties: value.label,
                operator: '= Equals',  // or any valid default
                value: []     // clear values after changing the property
            });
            setInputValue('')
        }
        else if (name === 'value') {
            const existing = selectedFilter?.value;
            const currentArray = Array.isArray(existing)
                ? existing
                : existing
                    ? [existing]
                    : [];
            // If already selected, remove it
            let updated: string[];
            if (currentArray.includes(value?.label)) {
                updated = currentArray.filter((v) => v !== value?.label);
            } else {
                updated = [...currentArray, value?.label];
            }
            setSelectedFilters((prev) => ({
                ...prev,
                [name]: updated,
            }) as SelectedFilter);
        } else {
            setSelectedFilters((prev) => ({
                ...prev,
                [name]: value.label
            }) as SelectedFilter)
        }
    }

    //for clicking delete button to check whether edit case or create case
    const resetFilters = () => {
        const index = appliedFilters?.findIndex(
            (s) => s?.properties === selectedFilter?.properties //checking if already exist
        );

        if (index !== -1 && index !== undefined) {
            removeFilter(index); //if already exist remove
        } else {
            const appliedFilterContainer = filterContainerRef.current
            const modal = filterContainerRef.current?.querySelector('.advance__filter__modal__container')
            setSelectedFilters(null)
            setAllFilters(allFilters)
            // applyFilter(allFilters) --> ad api was calling on delete call
            if (appliedFilterContainer) {
                appliedFilterContainer.classList.remove('active')
                modal?.classList.remove('active__modal')
            }
            setOpenOptions(null)
        }
        setInputValue('')
        setEditingFilter(null)
    }

    const handleApplyFilters = () => {
        if (!selectedFilter) {
            showToastMessage('error', 'Please select filter')
            return
        } else if (!selectedFilter?.properties) {
            showToastMessage('error', 'Please select property')
            return
        } else if (!selectedFilter?.operator) {
            showToastMessage('error', 'Please select operator')
            return
        } else if (!selectedFilter?.value || selectedFilter?.value?.length === 0) {
            showToastMessage('error', isInput ? 'Please enter a value' : 'Please select a value')
            return
        }
        let advanceFilters;
        if (editingFilter) { //if editing the filter
            const index = appliedFilters?.findIndex(
                (f) => f.properties === editingFilter.properties
            );
            if (index !== -1) {
                advanceFilters = [...appliedFilters];
                advanceFilters[index] = selectedFilter; //put at the original index
            } else {
                advanceFilters = [...appliedFilters, selectedFilter];
            }
        } else {
            advanceFilters = [...appliedFilters, selectedFilter]; //new filter put at last
        }
        setEditingFilter(null)
        setOpenOptions(null)


        const all_filters = {
            ...allFilters,
            'advanceFilters': advanceFilters
        }
        setAppliedFilters(advanceFilters)
        applyFilter(all_filters)
        setAllFilters(all_filters)
        setSelectedFilters(null)
        setInputValue('')
        const appliedFilterContainer = filterContainerRef.current
        const modal = filterContainerRef.current?.querySelector('.advance__filter__modal__container')
        if (!modal || !appliedFilterContainer) return
        appliedFilterContainer.classList.add('active')
        appliedFilterContainer.classList.add('filter__applied')
        modal.classList.remove('active__modal')
    }

    //directly remove filter
    const removeFilter = (index: number) => {
        setSelectedFilters(null)
        const advanceFilters = [...appliedFilters]
        advanceFilters.splice(index, 1)
        setAppliedFilters(advanceFilters)
        const all_filters: AllFilterType = {
            ...allFilters,
            'advanceFilters': advanceFilters
        }
        const appliedFilterContainer = filterContainerRef.current
        const modal = filterContainerRef.current?.querySelector('.advance__filter__modal__container')
        if (appliedFilterContainer && advanceFilters?.length === 0) {
            appliedFilterContainer.classList.remove('filter__applied')
            appliedFilterContainer.classList.remove('active')
            delete all_filters.advanceFilters
        }
        if (modal) {
            modal?.classList.remove('active__modal')
            setEditingFilter(null)
        }
        setInputValue('')
        applyFilter(all_filters)
        setAllFilters(all_filters)
        setOpenOptions(null)
    }

    const handleEditFilter = (filter, open, e) => {
        if (open) e.stopPropagation();
        setEditingFilter(filter)
        setSelectedFilters(filter)
        handleOpenModal(true, true, e)
        if (openOptions === open) { // already open return to avoid flicker
            return
        } else {
            setOpenOptions(open)
        }
    }

    const renderFilterButton = () => {
        const hideFilterIcon = (filterCase === 'simulate' || filterCase === 'ucp') && !isFilterExpanded;
        const isAddMoreState = appliedFilters.length > 0;
        const icon = appliedFilters.length > 0 ? <PlusSvg /> : (filterIcon || <PlusSvg />);
        const button = (
            <button
                className={`filter__icon__container ${hideFilterIcon ? 'hide__filter__button' : ''}`}
                onClick={(e) => {
                    handleOpenModal(true, false, e); // open popup, dont expand
                    setSelectedFilters(null);
                    setEditingFilter(null)
                }}
            >
                {icon}
            </button >
        );

        if (filterCase === 'validate' && !isAddMoreState) return button;

        return <CustomToolTip title={"Add Filter"} placement="top" key={"Add Filter"}>{button}</CustomToolTip>
    };

    const handleFilterCollapseClick = (e) => {
        if (filterCase === 'simulate' || filterCase === 'ucp') {
            if (appliedFilters?.length === 0) handleOpenModal(true, false, e)
            else if (appliedFilters?.length > 0) handleOpenModal(true, true, e)
        } else {
            handleOpenModal(false, true, e)
        }
    }

    return (
        <div className={`filter__container ${filterCase === 'validate' ? 'filter__container--validate' : ''}`} ref={filterContainerRef} style={{ position: 'relative' }}>
            {showFilterCount &&
                <div className={`filter__count__container ${appliedFilters?.length > 0 ? 'filterApplied' : ''}`} onClick={handleFilterCollapseClick}>
                    {filterIcon ?? `Filter +${appliedFilters?.length || 0}`}
                </div>
            }
            <div className={`applied__filters__container ${(!appliedFilters || appliedFilters.length === 0) ? 'empty' : ''}`}>
                {appliedFilters?.map((filter, index) => (
                    <div className='filter__item' key={index} onClick={(e) => { handleEditFilter(filter, null, e); }}>
                        <button className='d-flex gap-1 align-items-center p-0 bg-transparent border-0'>
                            {filter.properties && (
                                <FilterSpan
                                    title={Array.isArray(filter.properties) ? filter.properties.join(', ') : filter.properties}
                                    key={Array.isArray(filter.properties) ? filter.properties.join(', ') : filter.properties}
                                    onClick={(e) => handleEditFilter(filter, 'Property', e)}
                                >
                                    {Array.isArray(filter.properties)
                                        ? filter.properties.join(', ')
                                        : filter.properties}
                                </FilterSpan>
                            )}

                            {filter.operator && (
                                <FilterSpan
                                    title={filter.operator}
                                    key={filter.operator}
                                    onClick={(e) => handleEditFilter(filter, 'Operator', e)}
                                >
                                    {filter.operator === '= Equals' ? '=' : '≠'}
                                </FilterSpan>
                            )}

                            {filter.value && (
                                <FilterSpan
                                    title={Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                                    key={Array.isArray(filter.value) ? filter.value.join(', ') : filter.value}
                                    onClick={(e) => handleEditFilter(filter, 'Value', e)}
                                >
                                    {Array.isArray(filter.value)
                                        ? `+${filter?.value?.length}`
                                        : filter.value}
                                </FilterSpan>
                            )}
                        </button>

                        {filterCase === 'validate' ? (
                            <button className='remove__filter__icon'
                                onClick={(e) => {
                                    e.stopPropagation()
                                    removeFilter(index)
                                }}
                            >
                                <CrossSvg />
                            </button>
                        ) : (
                            <CustomToolTip title={"Remove Filter"} placement="top" key={"Remove Filter"}>
                                <button className='remove__filter__icon'
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        removeFilter(index)
                                    }}
                                >
                                    <CrossSvg />
                                </button>
                            </CustomToolTip>
                        )}
                    </div>
                ))}
            </div>
            {renderFilterButton()}
            <AdvanceFilterModal
                onSelect={onSelect}
                filterList={filterList}
                selectedFilter={selectedFilter}
                setSelectedFilters={setSelectedFilters}
                appliedFilters={appliedFilters}
                resetFilter={resetFilters}
                applyFilter={handleApplyFilters}
                editingFilter={editingFilter}
                setEditingFilter={setEditingFilter}
                openOptions={openOptions}
                setOpenOptions={setOpenOptions}
                popupPosition={popupPosition}
                inputValue={inputValue}
                setInputValue={setInputValue}
                filterCase={filterCase}
                containerRef={filterContainerRef}
            />
        </div>
    )
}

export default AdvanceFilter
